<template>
  <div class="flex flex-col w-full">
    <h1 class="text-center text-2xl font-bold mb-4">
      Gestión de Citas y Salud
    </h1>

    <!-- Historial y Próximas Citas -->
    <div class="mb-4 bg-white shadow rounded-lg">
      <div class="bg-blue-600 text-white p-4 rounded-t-lg">
        Historial y Próximas Citas
      </div>
      <div class="p-4">
        <div class="relative overflow-x-auto">
          <table
            v-if="citas && citas.length"
            class="w-full text-sm text-left text-gray-500"
          >
            <thead class="text-xs uppercase bg-gray-50 text-gray-700">
              <tr>
                <th scope="col" class="px-4 py-3">Fecha</th>
                <th scope="col" class="px-4 py-3">Hora</th>
                <th scope="col" class="px-4 py-3">Servicio</th>
                <th scope="col" class="px-4 py-3">Esteticista</th>
                <th scope="col" class="px-4 py-3">Estado</th>
                <th scope="col" class="px-4 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="cita in citas"
                :key="cita.id"
                class="bg-white border-b hover:bg-gray-50"
              >
                <td class="px-4 py-3">{{ cita.fecha }}</td>
                <td class="px-4 py-3">{{ cita.hora }}</td>
                <td class="px-4 py-3">
                  <ul v-if="cita.servicios && cita.servicios.length">
                    <li
                      v-for="servicio in cita.servicios"
                      :key="servicio.nombre"
                    >
                      {{ servicio.nombre }}
                    </li>
                  </ul>
                  <span v-else>No hay servicios asignados</span>
                </td>
                <td class="px-4 py-3">
                  <span v-if="cita.enfermeroNombre">{{
                    cita.enfermeroNombre
                  }}</span>
                  <span
                    v-else-if="cita.estado === 'pendiente'"
                    class="text-gray-500 italic"
                    >Por asignar</span
                  >
                  <span v-else class="text-gray-500">No asignado</span>
                </td>
                <td class="px-4 py-3">
                  <span>{{ cita.estado }}</span>
                  <span
                    v-if="cita.asistenciaConfirmada"
                    class="text-green-600 font-bold ml-2"
                  >
                    ✔ Confirmado — {{ cita.paciente?.nombre || "Paciente" }} —
                    {{ cita.fecha }} {{ cita.hora }}
                  </span>
                </td>
                <td class="px-4 py-3">
                  <div class="space-y-2">
                    <button
                      type="button"
                      :class="{
                        'bg-green-600 hover:bg-green-700 text-white cursor-pointer':
                          cita.estado !== 'pendiente',
                        'bg-gray-400 text-gray-600 cursor-not-allowed':
                          cita.estado === 'pendiente',
                      }"
                      class="font-semibold px-3 py-1 rounded w-full"
                      :disabled="cita.estado === 'pendiente'"
                      @click="confirmarAsistencia(cita)"
                    >
                      Confirmar Asistencia
                    </button>
                    <p
                      v-if="cita.estado === 'pendiente'"
                      class="text-xs text-gray-500 text-center"
                    >
                      Esperando aprobación del administrador
                    </p>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>

  <!-- Botón para Solicitar Nueva Cita -->
  <button
    @click="abrirModalSolicitarCita"
    class="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg py-3 px-4 mt-6 shadow-md transition duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-400"
  >
    Solicitar Nueva Cita
  </button>

  <!-- Modal para Solicitar Nueva Cita -->
  <div
    v-if="mostrarModalSolicitar"
    class="fixed inset-0 bg-gray-800 bg-opacity-60 flex items-center justify-center z-50 p-4"
  >
    <div
      class="bg-white rounded-xl p-6 w-full max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto"
    >
      <h5 class="text-lg font-bold mb-6 text-gray-800 text-center">
        Solicitar Nueva Cita
      </h5>

      <form @submit.prevent="solicitarCitaConPago" class="space-y-6">
        <!-- 1. Selecciona la fecha -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1"
            >Selecciona día</label
          >
          <input
            type="date"
            v-model="nuevaCita.fechaDeseada"
            @change="actualizarHoras"
            class="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-emerald-400 focus:outline-none"
            required
          />
        </div>

        <!-- 2. Selecciona servicio(s) -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2"
            >Servicios disponibles</label
          >
          <div v-if="serviciosAgrupados.length" class="space-y-3">
            <div
              v-for="grupo in serviciosAgrupados"
              :key="grupo.id"
              class="border border-gray-200 rounded-lg overflow-hidden"
            >
              <button
                type="button"
                class="w-full flex items-center justify-between px-4 py-3 bg-gray-50 text-left text-sm font-semibold text-gray-800 hover:bg-gray-100"
                @click="toggleCategoria(grupo.id)"
              >
                <span>{{ grupo.nombre }}</span>
                <svg
                  class="w-4 h-4 transition-transform"
                  :class="{ 'rotate-180': categoriasAbiertas[grupo.id] }"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div v-show="categoriasAbiertas[grupo.id]" class="divide-y divide-gray-100">
                <label
                  v-for="serv in grupo.servicios"
                  :key="serv.id"
                  class="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3 text-sm cursor-pointer hover:bg-gray-50"
                >
                  <div class="flex items-start gap-3">
                    <input
                      type="checkbox"
                      :value="serv"
                      v-model="nuevaCita.servicios"
                      @change="actualizarHoras"
                      class="mt-1 h-4 w-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                    />
                    <div>
                      <p class="font-medium text-gray-900">{{ serv.nombre }}</p>
                      <p class="text-xs text-gray-500" v-if="serv.descripcion">{{ serv.descripcion }}</p>
                    </div>
                  </div>
                  <div class="text-right sm:ml-auto">
                    <p class="font-semibold text-gray-900">
                      ${{ parseFloat(serv.precio || 0).toFixed(2) }}
                    </p>
                    <p class="text-xs text-gray-500">
                      {{ serv.duracion || 0 }} min
                    </p>
                  </div>
                </label>
              </div>
            </div>
          </div>
          <p v-else class="text-sm text-gray-500 mt-2">
            No hay servicios disponibles
          </p>
        </div>

        <!-- 3. Selecciona la hora -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1"
            >Selecciona hora</label
          >
          

          <select
            v-model="nuevaCita.horaDeseada"
            @change="validarHoraSeleccionada"
            class="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-emerald-400 focus:outline-none"
            required
          >
            <option disabled value="">Selecciona una hora</option>
            <option v-for="hora in horasDisponiblesCliente" :key="hora" :value="hora">
              {{ etiquetaHora(hora) }} ({{ getCantidadEsteticistasDisponibles(hora) }} disponibles)
            </option>
          </select>
          <p v-if="alertaDisponibilidad" class="mt-2 text-sm text-red-600">
            {{ alertaDisponibilidad }}
          </p>
        </div>

        <!-- Resumen y total -->
        <div class="mb-4 bg-gray-100 border border-gray-200 p-4 rounded-lg">
          <p class="text-sm font-medium text-gray-700 mb-2">Resumen:</p>
          <ul class="text-sm text-gray-600 list-disc ml-5 mb-2 space-y-1">
            <li v-for="s in serviciosValidos" :key="s.id">
              <span
                >{{ s.nombre }} - ${{ parseFloat(s.precio).toFixed(2) }}</span
              >
              <span
                v-if="s.tipo !== 'servicio'"
                class="text-blue-600 text-xs ml-1"
              >
                (+$0.00 imp.)
              </span>
            </li>
          </ul>
          <div class="text-sm text-gray-700 space-y-0.5">
            <p>
              Subtotal: <strong>${{ subtotalServicios.toFixed(2) }}</strong>
            </p>
            <p>
              Impuestos calculados:
              <strong>${{ impuestosServicios.toFixed(2) }}</strong>
            </p>
            <p>
              Total: <strong>${{ totalServicios.toFixed(2) }}</strong>
            </p>
            <p>
              Retención (50%):
              <strong>${{ (totalServicios * 0.5).toFixed(2) }}</strong>
            </p>
          </div>
        </div>
        <!-- Stripe Payment -->
        <div class="mb-6">
          <label
            for="nombre-tarjeta"
            class="block text-sm font-medium text-gray-700 mb-2"
          >
            Nombre en la tarjeta
          </label>
          <input
            id="nombre-tarjeta"
            v-model="nombreTarjeta"
            type="text"
            class="w-full border border-gray-300 rounded px-3 py-2 mb-4"
            placeholder="Nombre del titular"
            autocomplete="cc-name"
          />

          <!--  Etiqueta para el campo de Stripe -->
          <label
            for="card-element"
            class="block text-sm font-medium text-gray-700 mb-2"
          >
            Datos de tarjeta
          </label>

          <!--Stripe Card Element -->
          <div
            id="card-element"
            ref="stripeCard"
            class="p-3 border border-gray-300 rounded bg-white shadow-sm"
          ></div>

          <!-- Stripe error messages -->
          <div id="card-errors" class="text-sm text-red-600 mt-2">
            {{ stripeError }}
          </div>
        </div>
        <!-- Checkbox estilo Flowbite -->
        <div class="flex items-start mb-4">
          <div class="flex items-center h-5">
            <input
              id="acepta-politica"
              type="checkbox"
              v-model="acepta_politica"
              class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
              required
            />
          </div>
          <label
            for="acepta-politica"
            class="ml-2 text-sm font-medium text-gray-900"
          >
            Acepto la
            <button
              type="button"
              class="text-blue-600 hover:underline"
              @click="mostrarPolitica = true"
            >
              política de cancelación
            </button>
            : cambios o cancelaciones deben hacerse con 48h de anticipación. En
            caso de no asistir, el depósito no es reembolsable.
          </label>
        </div>
       

        <!--  Botón que llama tu función -->
        <button
          type="button"
          @click="solicitarCitaConPago"
          class="w-full bg-green-600 text-white py-2 rounded-full font-bold hover:bg-green-700 transition"
        >
          Pagar y Solicitar Cita
        </button>

        <button
          type="button"
          @click="cerrarModalSolicitarCita"
          class="w-full mt-2 bg-red-600 text-white py-2 rounded-full font-bold hover:bg-red-700"
        >
          Cancelar
        </button>
      </form>
    </div>
  </div>

  <!-- Modal para Reprogramar la Cita -->
  <div
    v-if="mostrarModalReprogramar"
    class="fixed inset-0 bg-gray-700 bg-opacity-50 flex items-center justify-center"
  >
    <div class="bg-white rounded-lg p-6 w-full max-w-md shadow-lg">
      <h5 class="text-lg font-bold mb-4">Reprogramar Cita</h5>
      <input
        type="date"
        v-model="nuevaFecha"
        class="w-full border rounded px-3 py-2 text-gray-700 focus:ring-blue-500 focus:border-blue-500 my-2"
      />
      <TimePicker v-model="nuevaHora" placeholder="Seleccionar hora" />
      <button
        @click="confirmarReprogramacion"
        class="text-white bg-blue-700 hover:bg-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-300 font-medium rounded-full text-sm px-5 py-2.5 w-full"
      >
        Confirmar
      </button>
      <button
        @click="cerrarModalReprogramar"
        class="text-white bg-red-700 hover:bg-red-800 focus:outline-none focus:ring-4 focus:ring-red-300 font-medium rounded-full text-sm px-5 py-2.5 w-full"
      >
        Cancelar
      </button>
    </div>
  </div>

  <!-- Modal de política -->
  <div
    v-if="mostrarPolitica"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
  >
    <div
      class="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6"
    >
      <h2 class="text-xl font-bold mb-4">Política de Cancelación</h2>

      <!-- Aquí tu componente con el texto legal -->
      <PoliticaCancelacion />

      <!-- Botón de cerrar -->
      <div class="mt-6 flex justify-end">
        <button
          @click="mostrarPolitica = false"
          class="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
        >
          Cerrar
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { nextTick } from "vue";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  addDoc,
  setDoc,
  Timestamp,
} from "firebase/firestore";
import { db } from "@/firebase";
import TimePicker from "./TimePicker.vue";
import { getFunctions, httpsCallable } from "firebase/functions";
import { loadStripe } from "@stripe/stripe-js";
import { generarTimeSlots, sumarMinutos, rangosSeSolapan } from "@/utils/timeUtils";
import { formatIsoPrTime } from "@/utils/prTime";
import PoliticaCancelacion from "@/components/views/paciente/politica-cancelacion.vue";

let stripe;
let cardElement;

export default {
  components: { TimePicker, PoliticaCancelacion },
  data() {
    return {
      acepta_politica: false,
      mostrarPolitica: false,
      paciente: {
        nombre: "Nombre de Ejemplo",
        direccion: "",
        telefono: "",
        fotoPerfil: "",
      },
      citas: [],
      citasSistema: [],
      citasProximas: [],
      nuevaCita: { fechaDeseada: "", horaDeseada: "", servicios: [] },
      mostrarModalSolicitar: false,
      mostrarModalReprogramar: false,
      citaSeleccionada: null,
      nuevaFecha: "",
      nuevaHora: "",
      enfermeros: [],
      serviciosDisponibles: [],
      categoriasMap: {},
      categoriasAbiertas: {},
      servicios: [],
      impuestosTodos: [],
      horasDisponiblesCliente: [],
      disponibilidadHoras: {}, // Almacena la disponibilidad por hora
      disponibilidadPorEnfermeroCruda: null,
      disponibilidadCachePorFecha: {},
      cargandoDisponibilidadCliente: false,
      stripeError: "",
      nombreTarjeta: "",
      alertaDisponibilidad: "",
    };
  },

  computed: {
    serviciosValidos() {
      return (this.nuevaCita.servicios || []).filter(
        (s) => s.precio !== undefined && !isNaN(s.precio)
      );
    },
    subtotalServicios() {
      return this.serviciosValidos.reduce(
        (sum, s) => sum + parseFloat(s.precio),
        0
      );
    },
    impuestosServicios() {
      // Los servicios NO tienen impuestos aplicados
      return 0;
    },
    totalServicios() {
      return this.subtotalServicios + this.impuestosServicios;
    },
    serviciosAgrupados() {
      if (!Array.isArray(this.serviciosDisponibles)) return [];
      const grupos = {};
      this.serviciosDisponibles.forEach((serv) => {
        const categoriaId = serv.categoria || "sin-categoria";
        if (!grupos[categoriaId]) {
          grupos[categoriaId] = {
            id: categoriaId,
            nombre: this.categoriasMap[categoriaId] || "Otros servicios",
            servicios: [],
          };
        }
        grupos[categoriaId].servicios.push(serv);
      });
      return Object.values(grupos).sort((a, b) => a.nombre.localeCompare(b.nombre));
    },
  },

  methods: {
    
    formatearHoraAMPM(hora24) {
      if (!hora24 || typeof hora24 !== "string" || !hora24.includes(":")) {
        console.warn(" Hora inválida:", hora24);
        return "Hora inválida";
      }
      const [horaStr, minutoStr] = hora24.split(":");
      const hora = Number(horaStr);
      const minutos = minutoStr?.padStart(2, "0") || "00";
      if (!Number.isFinite(hora)) return hora24;
      const esPM = hora >= 12;
      const hora12 = ((hora + 11) % 12) + 1;
      return `${hora12}:${minutos} ${esPM ? "PM" : "AM"}`;
    },
    etiquetaHora(hora) {
      return this.formatearHoraAMPM(hora);
    },

    convertirHoraAMPMa24(entrada) {
      if (!entrada || typeof entrada !== "string") return "";
      const valorLimpio = entrada.trim();
      if (!valorLimpio) return "";

      // Formato 24h directo => 13:30
      if (/^\d{1,2}:\d{2}$/.test(valorLimpio)) {
        const [h, m] = valorLimpio.split(":").map(Number);
        if (!Number.isFinite(h) || !Number.isFinite(m)) return "";
        return `${h.toString().padStart(2, "0")}:${m
          .toString()
          .padStart(2, "0")}`;
      }

      // Formatos con AM/PM, incluso si traen texto extra ("8:00 PM (2)")
      const match = valorLimpio.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
      if (!match) {
        console.warn("No se pudo interpretar hora AM/PM:", entrada);
        return "";
      }

      let [, horasStr, minutosStr, sufijo] = match;
      let horas = parseInt(horasStr, 10);
      const minutos = parseInt(minutosStr, 10);
      const sufijoUp = sufijo.toUpperCase();

      if (sufijoUp === "PM" && horas !== 12) horas += 12;
      if (sufijoUp === "AM" && horas === 12) horas = 0;

      return `${horas.toString().padStart(2, "0")}:${minutos
        .toString()
        .padStart(2, "0")}`;
    },

    handleClickTest(cita) {
      console.log(" CLICK ejecutado para:", cita.id);

      if (typeof this.confirmarAsistencia !== "function") {
        console.warn(" confirmarAsistencia no es una función");
        return;
      }

      this.confirmarAsistencia(cita);
    },

    async actualizarHoras() {
      const disponibilidad = await this.generarHorasReales();
      if (!disponibilidad || Object.keys(disponibilidad).length === 0) {
        this.horasDisponiblesCliente = [];
        this.disponibilidadHoras = {};
        this.alertaDisponibilidad = "No hay horarios disponibles para la fecha o servicios elegidos.";
        return;
      }

      // Almacena la disponibilidad completa y extrae las horas disponibles
      this.disponibilidadHoras = disponibilidad;
      this.horasDisponiblesCliente = Object.keys(disponibilidad).sort();
      this.alertaDisponibilidad =
        this.horasDisponiblesCliente.length === 0
          ? "No hay horarios disponibles para la fecha o servicios elegidos."
          : "";

      console.log(" Horas disponibles:", this.horasDisponiblesCliente);
    },
    async cargarDisponibilidadCliente(force = false) {
      const activos = this.enfermeros.filter((e) => e.activo !== false);
      if (!activos.length) {
        this.disponibilidadPorEnfermeroCruda = {};
        return {};
      }

      if (!force && this.disponibilidadPorEnfermeroCruda) {
        return this.disponibilidadPorEnfermeroCruda;
      }

      this.cargandoDisponibilidadCliente = true;
      try {
        const funciones = getFunctions();
        const callable = httpsCallable(funciones, "listarDisponibilidadCallable");
        const { data } = await callable({
          uids: activos.map((e) => e.id),
          semanas: 8,
          soloFuturos: false,
          origenesPermitidos: ["auto", "excepcion", "manual"],
        });
        this.disponibilidadPorEnfermeroCruda = data?.porEnfermero || {};
        this.disponibilidadCachePorFecha = {};
        return this.disponibilidadPorEnfermeroCruda;
      } catch (error) {
        console.error("Error al listar disponibilidad:", error);
        this.alertaDisponibilidad =
          "No se pudo cargar la disponibilidad. Intenta nuevamente en unos segundos.";
        this.disponibilidadPorEnfermeroCruda = {};
        return {};
      } finally {
        this.cargandoDisponibilidadCliente = false;
      }
    },
    async obtenerBloquesParaFecha(fechaKey) {
      if (!fechaKey) return [];
      if (this.disponibilidadCachePorFecha[fechaKey]) {
        return this.disponibilidadCachePorFecha[fechaKey];
      }

      const porEnfermero =
        this.disponibilidadPorEnfermeroCruda ||
        (await this.cargarDisponibilidadCliente());

      const bloques = [];
      Object.entries(porEnfermero || {}).forEach(([uid, lista]) => {
        (lista || []).forEach((bloque) => {
          const fechaBloque =
            bloque.fechaKey || (bloque.start || "").split("T")[0] || "";
          if (fechaBloque !== fechaKey) return;
          const inicio = formatIsoPrTime(bloque.start);
          const fin = formatIsoPrTime(bloque.end);
          if (!inicio || !fin) return;
          bloques.push({
            ...bloque,
            enfermeroId: bloque.enfermeroId || uid,
            inicioLocal: inicio,
            finLocal: fin,
          });
        });
      });

      this.disponibilidadCachePorFecha = {
        ...this.disponibilidadCachePorFecha,
        [fechaKey]: bloques,
      };
      return bloques;
    },
    generarSlots(inicio, fin, duracion, fechaBase) {
      return generarTimeSlots(inicio, fin, duracion, fechaBase, true);
    },
    tieneConflictoCita(horaInicio, duracion, citas, enfermeroId) {
      if (!Array.isArray(citas) || !enfermeroId) return false;
      const horaFin = sumarMinutos(horaInicio, duracion);
      return citas.some((cita) => {
        if (!cita || cita.enfermeroId !== enfermeroId) return false;
        const inicioCita =
          cita.hora24 ||
          this.convertirHoraAMPMa24(cita.hora || cita.horaDeseada);
        if (!inicioCita) return false;
        const finCita = sumarMinutos(inicioCita, cita.duracion || 60);
        return rangosSeSolapan(horaInicio, horaFin, inicioCita, finCita);
      });
    },
    toggleCategoria(id) {
      this.categoriasAbiertas = {
        ...this.categoriasAbiertas,
        [id]: !this.categoriasAbiertas[id],
      };
    },

    async cargarCitas() {
      try {
        const pacienteId = this.$route.params.pacienteId;
        const querySnapshot = await getDocs(collection(db, "citasGlobales"));

        const citas = await Promise.all(
          querySnapshot.docs.map(async (doc) => {
            const data = doc.data();

            //  Traer paciente
            let pacienteData = {};
            try {
              const pacienteRef = doc(db, "pacientes", data.pacienteId);
              const pacienteSnap = await getDoc(pacienteRef);
              if (pacienteSnap.exists()) {
                pacienteData = pacienteSnap.data();
              }
            } catch (e) {
              console.warn(" No se pudo obtener paciente:", e);
            }

            // Traer nombre del esteticista
            let enfermeroNombre = "";
            if (data.enfermeroId || data.enfermero) {
              try {
                const enfermeroRef = doc(
                  db,
                  "enfermeros",
                  data.enfermeroId || data.enfermero
                );
                const enfermeroSnap = await getDoc(enfermeroRef);
                if (enfermeroSnap.exists()) {
                  const enfermeroData = enfermeroSnap.data();
                  enfermeroNombre = `${enfermeroData.nombre || ""} ${
                    enfermeroData.apellido || ""
                  }`.trim();
                }
              } catch (e) {
                console.warn(" No se pudo obtener esteticista:", e);
              }
            }

            return {
              id: doc.id,
              fecha: data.fecha,
              hora: data.hora,
              codigo: data.codigoEmail,
              estado: data.estado,
              pacienteId: data.pacienteId,
              servicios: Array.isArray(data.servicios) ? data.servicios : [],
              asistenciaConfirmada: data.asistenciaConfirmada || false,
              paciente: pacienteData,
              enfermeroId: data.enfermeroId || data.enfermero || null,
              enfermeroNombre: enfermeroNombre,

              duracion: (data.servicios || []).reduce(
                (acc, s) => acc + (s.duracion || 0),
                0
              ),
            };
          })
        );

        const citasLimpias = citas.filter((c) => c !== null);
        this.citasSistema = citasLimpias;
        this.citas = citasLimpias.filter((c) => c.pacienteId === pacienteId);
      } catch (error) {
        console.error("Error al cargar las citas:", error);
      }
    },

    async cargarImpuestos() {
      const snapshot = await getDocs(collection(db, "impuestos"));
      this.impuestosTodos = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    },

    puedeConfirmar(cita) {
      const fechaHora = new Date(`${cita.fecha}T${cita.hora}`);
      const ahora = new Date();
      const diferenciaMs = fechaHora - ahora;
      const horasFaltantes = diferenciaMs / (1000 * 60 * 60);
      return horasFaltantes > 48;
    },

    async confirmarAsistencia(cita) {
      console.log("🔧 Ejecutando confirmación para", cita.id);

      // Verificar si la cita está pendiente de aprobación
      if (cita.estado === "pendiente") {
        console.warn(
          " No se puede confirmar asistencia: cita pendiente de aprobación"
        );
        alert(
          "No puedes confirmar tu asistencia hasta que el administrador apruebe la cita."
        );
        return;
      }

      try {
        const citaRef = doc(db, "citasGlobales", cita.id);
        await updateDoc(citaRef, {
          asistenciaConfirmada: true,
          confirmacionCliente: true,
        });

        this.citas = this.citas.map((c) =>
          c.id === cita.id ? { ...c, asistenciaConfirmada: true } : c
        );
        this.citasSistema = this.citasSistema.map((c) =>
          c.id === cita.id ? { ...c, asistenciaConfirmada: true } : c
        );

        //  Obtener nombre del paciente directamente
        let nombre = "Cliente";
        try {
          const pacienteRef = doc(db, "pacientes", cita.pacienteId);
          const pacienteSnap = await getDoc(pacienteRef);
          if (pacienteSnap.exists()) {
            const pacienteData = pacienteSnap.data();
            nombre = `${pacienteData.nombre || ""} ${
              pacienteData.apellido || ""
            }`.trim();
          }
        } catch (e) {
          console.warn(" No se pudo obtener nombre del paciente:", e);
        }

        const sendSms = httpsCallable(getFunctions(), "sendSms");
        const mensaje = `📢 ${nombre} ha confirmado su asistencia para el ${cita.fecha} a las ${cita.hora}.`;

        await sendSms({
          to: "+17874159799",
          message: mensaje,
        });

        console.log(" SMS enviado al admin:", mensaje);
      } catch (e) {
        console.error(" Error confirmando asistencia o enviando SMS:", e);
      }
    },

    async cargarDatosPaciente() {
      const pacienteId = this.$route.params.pacienteId;
      try {
        const docRef = doc(db, "pacientes", pacienteId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          this.paciente = docSnap.data();
        }
      } catch (error) {
        console.error("Error al cargar los datos del paciente:", error);
      }
    },

    cargarCitasProximas() {
      const hoy = new Date();
      this.citasProximas = this.citas.filter((cita) => {
        const fechaCita = new Date(cita.fecha);
        const diasDiferencia = (fechaCita - hoy) / (1000 * 60 * 60 * 24);
        return diasDiferencia >= 0 && diasDiferencia <= 7;
      });
    },

    async abrirModalSolicitarCita() {
      this.mostrarModalSolicitar = true;

      await this.cargarEnfermeros(); //  Asegúrate de que enfermeros están listos
      await this.cargarCitas(); //  Asegúrate de tener citas actualizadas

      this.$nextTick(() => {
        if (
          this.nuevaCita.fechaDeseada &&
          this.nuevaCita.servicios.length > 0
        ) {
          this.actualizarHoras(); //  Recalcula solo si hay datos
        } else {
          this.horasDisponiblesCliente = [];
        }
      });
    },

    cerrarModalSolicitarCita() {
      this.mostrarModalSolicitar = false;
    },

    abrirModalReprogramar(cita) {
      this.citaSeleccionada = cita;
      this.mostrarModalReprogramar = true;
    },

    cerrarModalReprogramar() {
      this.mostrarModalReprogramar = false;
      this.citaSeleccionada = null;
    },

    async cargarEnfermeros() {
      try {
        const querySnapshot = await getDocs(collection(db, "enfermeros"));
        this.enfermeros = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          nombre: doc.data().nombre,
          servicios: doc.data().servicios || [], //  agrega esto
          activo: doc.data().activo !== false, //  opcional: solo si filtras por activos
        }));
      } catch (error) {
        console.error("Error al cargar los enfermeros:", error);
      }
    },

    async solicitarCitaConPago() {
      const pacienteId = this.$route.params.pacienteId;
      
      try {
        // Espera a que el DOM se actualice completamente
        await nextTick();

        const elementContainer = document.getElementById("card-element");

        if (!elementContainer) {
          console.error(" #card-element no está en el DOM.");
          return alert("Error interno: no se encontró el campo de tarjeta.");
        }

        if (!stripe) {
          const publicKey = process.env.VUE_APP_STRIPE_PUBLIC_KEY;
          stripe = await loadStripe(publicKey);
        }

        const elements = stripe.elements();

        // Monta solo si aún no existe un iframe activo de Stripe
        if (!cardElement || !elementContainer.querySelector("iframe")) {
          cardElement = elements.create("card");
          cardElement.mount("#card-element");
        }

        const pacienteDoc = await getDoc(doc(db, "pacientes", pacienteId));
        if (!pacienteDoc.exists()) throw new Error("Paciente no encontrado.");

        const pacienteData = pacienteDoc.data();

        //  Paso 1: Convertimos la hora AM/PM del selector a formato 24h
        const horaInicioAMPM = this.nuevaCita.horaDeseada; // ej: "2:00 PM"
        const horaInicio = this.convertirHoraAMPMa24(
          this.nuevaCita.horaDeseada
        );

        //  Duración total de los servicios seleccionados
        const duracionTotal = (this.nuevaCita.servicios || []).reduce(
          (acc, s) => acc + (s.duracion || 0),
          0
        );

        // ⏱ Hora fin estimada de la nueva cita
        const horaFin = sumarMinutos(horaInicio, duracionTotal);

        //  Paso 2: Verificar solapamientos por enfermero
        const disponibles = this.enfermeros.filter((enf) => {
          const conflictos = this.citasSistema.filter((c) => {
            const horaInicioCita = this.convertirHoraAMPMa24(c.hora); // 👈 FIX
            const finCita = sumarMinutos(horaInicioCita, c.duracion || 60);
            const seSolapa = rangosSeSolapan(
              horaInicio,
              horaFin,
              horaInicioCita,
              finCita
            );

            console.log(
              ` Comparando con cita de ${c.enfermeroId} — ${c.fecha} ${horaInicioCita}-${finCita}:`,
              seSolapa
            );

            return (
              c.enfermeroId === enf.id &&
              c.fecha === this.nuevaCita.fechaDeseada &&
              seSolapa
            );
          });

          return conflictos.length === 0;
        });

        //  Si no hay ningún enfermero libre, cancelar
        if (disponibles.length === 0) {
          alert(
            ` No hay disponibilidad para ${this.nuevaCita.fechaDeseada} a las ${horaInicioAMPM}. Selecciona otra hora.`
          );
          return;
        }

        if (!this.acepta_politica) {
          alert("Debes aceptar la política de cancelación para continuar.");
          return;
        }

        const funciones = getFunctions();
        const crearIntentoPago = httpsCallable(funciones, "crearIntentoPago");

        const { data } = await crearIntentoPago({
          servicios: this.nuevaCita.servicios,
          pacienteId,
        });

        const { client_secret, intent_id, balancePendiente } = data;

        const result = await stripe.confirmCardPayment(client_secret, {
          payment_method: {
            card: cardElement,
            billing_details: { name: pacienteData.nombre },
          },
        });

        if (result.error) {
          console.error(" Error de Stripe:", result.error.message);
          alert(`Error de pago: ${result.error.message}`);
          return;
        }

        if (result.paymentIntent.status !== "requires_capture") {
          alert("El pago no se pudo retener correctamente.");
          return;
        }

        let horaFormateada = "";

        try {
          const rawHora = this.nuevaCita.horaDeseada;

          if (rawHora instanceof Date) {
            let hours = rawHora.getHours();
            const minutes = rawHora.getMinutes().toString().padStart(2, "0");
            const ampm = hours >= 12 ? "PM" : "AM";
            hours = hours % 12 || 12;
            horaFormateada = `${hours}:${minutes} ${ampm}`;
          } else if (
            typeof rawHora === "string" &&
            rawHora.match(/\d{1,2}:\d{2}/)
          ) {
            horaFormateada = rawHora;
          } else {
            throw new Error(" Formato de hora inválido.");
          }
        } catch (e) {
          console.error(" Error al formatear la hora:", e);
          alert("Selecciona una hora válida antes de continuar.");
          return;
        }

        //  Guardar cita con el intent_id
        // Asignar el primer esteticista disponible
        const esteticistasDisponibles =
          await this.obtenerEsteticistasDisponibles();
        const esteticista =
          esteticistasDisponibles.length > 0
            ? esteticistasDisponibles[0]
            : null;

        if (!esteticista) {
          alert(
            "No hay esteticistas disponibles para esa hora. Por favor selecciona otra hora."
          );
          return;
        }

        const citaData = {
          pacienteId,
          fecha: this.nuevaCita.fechaDeseada, // ya viene como string "YYYY-MM-DD"
          hora: horaFormateada,
          horaFin: horaFin,
          servicios: this.nuevaCita.servicios,
          estado: "pendiente",
          pagada: false,
          paymentIntentId: intent_id,
          balancePendiente,
          duracion: duracionTotal,
          paciente: pacienteData,
          enfermero: esteticista.id, // Asignar el esteticista automáticamente
          enfermeroId: esteticista.id,
          creado: Timestamp.now(), // esto sí es 100% compatible con Firestore
        };

        const citaRef = await addDoc(
          collection(db, `pacientes/${pacienteId}/citas`),
          citaData
        );

        //  Reutilizamos el mismo ID en citasGlobales
        await setDoc(doc(db, "citasGlobales", citaRef.id), {
          ...citaData,
        });

        alert(" Cita solicitada. El pago fue retenido correctamente.");
        this.cerrarModalSolicitarCita();
        this.nuevaCita = {
          fechaDeseada: "",
          horaDeseada: "",
          motivo: "",
          servicios: [],
        };
        await this.cargarCitas();
      } catch (error) {
        console.error(" Error en solicitarCitaConPago:", error);
        alert("Hubo un error al solicitar la cita.");
      }
    },

    async confirmarReprogramacion() {
      if (this.nuevaFecha && this.nuevaHora && this.citaSeleccionada) {
        try {
          const citaRef = doc(db, "citasGlobales", this.citaSeleccionada.id);
          await updateDoc(citaRef, {
            fecha: this.nuevaFecha,
            hora: this.nuevaHora,
          });

          alert("Cita reprogramada con éxito.");
          this.cerrarModalReprogramar();
          await this.cargarCitas();
        } catch (error) {
          console.error("Error al reprogramar la cita:", error);
          alert(
            "Hubo un error al reprogramar la cita. Por favor, inténtalo de nuevo."
          );
        }
      } else {
        alert("Por favor, asegúrate de que la fecha y hora estén completas.");
      }
    },

    async generarHorasReales() {
      const fecha = this.nuevaCita.fechaDeseada;
      const serviciosSeleccionados = this.nuevaCita.servicios;

      if (
        !fecha ||
        !Array.isArray(serviciosSeleccionados) ||
        serviciosSeleccionados.length === 0
      ) {
        console.warn(" No hay servicios seleccionados o fecha inválida");
        return null;
      }

      const serviciosValidos = serviciosSeleccionados.filter(
        (s) => s?.duracion && !isNaN(s.duracion)
      );
      if (serviciosValidos.length === 0) {
        console.warn(" No hay servicios válidos con duración");
        return null;
      }

      const duracionTotal = serviciosValidos.reduce(
        (acc, s) => acc + s.duracion,
        0
      );
      if (!duracionTotal) {
        console.warn(" Duración total inválida:", duracionTotal);
        return null;
      }

      const enfermerosActivos = this.enfermeros.filter((e) => e.activo !== false);
      if (!enfermerosActivos.length) {
        console.warn("⛔ No hay enfermeros activos en el sistema");
        return null;
      }

      await this.cargarDisponibilidadCliente();
      const bloques = await this.obtenerBloquesParaFecha(fecha);
      if (!bloques.length) {
        console.warn(" No hay bloques disponibles para la fecha:", fecha);
        return {};
      }

      const citasFiltradas = Array.isArray(this.citasSistema)
        ? this.citasSistema.filter((c) => c.fecha === fecha)
        : [];

      const resultado = {};
      bloques.forEach((bloque) => {
        const slots = this.generarSlots(
          bloque.inicioLocal,
          bloque.finLocal,
          duracionTotal,
          fecha
        );
        slots.forEach((slot) => {
          if (this.tieneConflictoCita(slot, duracionTotal, citasFiltradas, bloque.enfermeroId)) {
            return;
          }
          if (!resultado[slot]) resultado[slot] = [];
          if (!resultado[slot].includes(bloque.enfermeroId)) {
            resultado[slot].push(bloque.enfermeroId);
          }
        });
      });

      this.disponibilidadHoras = resultado;
      return resultado;
    },

    async obtenerEsteticistasDisponibles() {
      try {
        const disponibilidad =
          Object.keys(this.disponibilidadHoras || {}).length > 0
            ? this.disponibilidadHoras
            : await this.generarHorasReales();
        const horaSeleccionada = this.convertirHoraAMPMa24(
          this.nuevaCita.horaDeseada
        );

        if (!disponibilidad || !disponibilidad[horaSeleccionada]) {
          return [];
        }

        const esteticistasIds = disponibilidad[horaSeleccionada];
        const esteticistasDisponibles = [];

        for (const esteticista of this.enfermeros) {
          if (esteticistasIds.includes(esteticista.id)) {
            esteticistasDisponibles.push(esteticista);
          }
        }

        return esteticistasDisponibles;
      } catch (error) {
        console.error("Error obteniendo esteticistas disponibles:", error);
        return [];
      }
    },

    getCantidadEsteticistasDisponibles(hora) {
      if (!this.disponibilidadHoras || !this.disponibilidadHoras[hora]) {
        return 0;
      }
      return this.disponibilidadHoras[hora].length;
    },
    validarHoraSeleccionada() {
      if (!this.nuevaCita.horaDeseada) return;
      const disponibles = this.disponibilidadHoras[this.nuevaCita.horaDeseada];
      if (!disponibles || disponibles.length === 0) {
        this.alertaDisponibilidad = "La hora seleccionada ya no está disponible. Escoge otra.";
        this.nuevaCita.horaDeseada = "";
      } else {
        this.alertaDisponibilidad = "";
      }
    },

    async cargarCategorias() {
      try {
        const snap = await getDocs(collection(db, "categorias"));
        const map = {};
        snap.docs.forEach((docSnap) => {
          const data = docSnap.data() || {};
          map[docSnap.id] = data.nombre || "Sin categoría";
        });
        this.categoriasMap = map;
      } catch (error) {
        console.warn("No se pudieron cargar categorías:", error);
        this.categoriasMap = {};
      }
    },

    async cargarServiciosDesdePOS() {
      const LASER_CATEGORY_ID = "S17C2GIVnq1NdjayK0mW"; // tu categoría "Depilación Laser"

      const pacienteId = this.$route.params.pacienteId;
      const pacienteSnap = await getDoc(doc(db, "pacientes", pacienteId));
      const pdata = pacienteSnap.exists() ? pacienteSnap.data() : {};

      // Permisos del paciente
      const serviciosPermitidosIds = Array.isArray(pdata.serviciosPermitidos)
        ? pdata.serviciosPermitidos
        : [];
      const permisosLogicos = Array.isArray(pdata.permisos)
        ? pdata.permisos
        : [];
      const esLaserPorCampoServicio = pdata.servicio === "depilacion_laser";
      const pacienteTienePermisoLaser =
        esLaserPorCampoServicio || permisosLogicos.includes("laser");

      await this.cargarCategorias();
      const snap = await getDocs(collection(db, "productos"));
      this.serviciosDisponibles = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((p) => {
          // Visibilidad base
          const esServicio = p?.tipo === "servicio";
          const visible = p?.visible !== false;
          const activo = p?.activo !== false;
          const mostrarEn = (p?.mostrarEn || "").toString().toLowerCase();
          const visibleWeb =
            p?.mostrarWeb === true ||
            ["ambos", "ecommerce"].includes(mostrarEn);

          if (!(esServicio && visible && activo && visibleWeb)) return false;

          // 🔒 REGLA: cualquier producto cuyo nombre contenga "laser/láser" o cuya categoría sea la de láser
          const nombre = (p?.nombre || "").toString().toLowerCase();
          const esLaserPorNombre = /(láser|laser)/i.test(nombre);
          const esLaserPorCategoria = p?.categoria === LASER_CATEGORY_ID;
          const esLaser = esLaserPorNombre || esLaserPorCategoria;

          if (!esLaser) {
            // No es láser → pasa directo
            return true;
          }

          // Es láser → necesita permiso
          const aprobadoPorRol = pacienteTienePermisoLaser;
          const aprobadoPorId = serviciosPermitidosIds.includes(p.id);
          const aprobado = aprobadoPorRol || aprobadoPorId;

          console.log(
            `🔒 LÁSER "${p.nombre}" [${p.id}] cat=${p?.categoria} -> permisoRol=${aprobadoPorRol}, permisoId=${aprobadoPorId}, aprobado=${aprobado}`
          );

          return aprobado;
        })
        .sort((a, b) => (this.categoriasMap[a.categoria] || "").localeCompare(this.categoriasMap[b.categoria] || ""));

      console.log(
        "Servicios visibles:",
        this.serviciosDisponibles.map((s) => s.nombre)
      );
    },
  },

  mounted: async function () {
    console.log("Componente montado correctamente");

    const pacienteId = this.$route.params.pacienteId;

    //  1. Sincronizar servicios láser si aplica
    try {
      const funciones = getFunctions();
      const syncLaser = httpsCallable(funciones, "sincronizarServiciosLaser");
      await syncLaser({ pacienteId });
      console.log(" Servicios láser sincronizados para paciente", pacienteId);
    } catch (error) {
      console.warn(" Error al sincronizar servicios láser:", error);
    }

    // 2. Luego sigue el flujo normal
    await this.cargarDatosPaciente();
    await this.cargarCitas();
    this.cargarCitasProximas();
    await this.cargarEnfermeros();
    await this.cargarServiciosDesdePOS(); // ← ya usará datos actualizados
    this.horariosDisponibles = await this.generarHorasReales();
    await this.cargarImpuestos();
  },

  watch: {
    mostrarModalSolicitar: {
      immediate: false,
      handler: async function (val) {
        if (val) {
          await this.$nextTick(); // Espera a que el DOM renderice
          const publicKey = process.env.VUE_APP_STRIPE_PUBLIC_KEY;
          if (!publicKey) {
            console.error(
              " No se encontró VUE_APP_STRIPE_PUBLIC_KEY en el .env"
            );
            return;
          }

          stripe = await loadStripe(publicKey);
          const elements = stripe.elements();
          const el = document.getElementById("card-element");

          if (el && !el.querySelector("iframe")) {
            cardElement = elements.create("card");
            cardElement.mount("#card-element");
          }
        }
      },
    },
    "nuevaCita.fechaDeseada"(nuevaFecha) {
      if (
        nuevaFecha &&
        Array.isArray(this.nuevaCita.servicios) &&
        this.nuevaCita.servicios.length
      ) {
        this.actualizarHoras();
      } else {
        this.horasDisponiblesCliente = [];
        this.disponibilidadHoras = {};
      }
    },
    "nuevaCita.servicios": {
      handler(nuevosServicios) {
        if (
          Array.isArray(nuevosServicios) &&
          nuevosServicios.length > 0 &&
          this.nuevaCita.fechaDeseada
        ) {
          this.actualizarHoras();
        } else {
          this.horasDisponiblesCliente = [];
          this.disponibilidadHoras = {};
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.table {
  font-size: 0.9rem;
  width: 100%;
}

.card {
  margin-bottom: 1rem;
}

.card-header {
  font-weight: bold;
  font-size: 1.1rem;
}

.action-button {
  border-radius: 20px;
  font-weight: bold;
  width: 100%;
  /* Asegura que el botón ocupe el ancho completo en dispositivos móviles */
  margin-bottom: 8px;
}

.perfil-paciente {
  display: flex;
  align-items: center;
  background-color: #f4f6f9;
  padding: 15px;
  border-radius: 5px;
  flex-direction: column;
  text-align: center;
}

.foto-perfil {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border: 2px solid #007bff;
  margin-bottom: 10px;
}

/* Estilos del modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.modal-content {
  background: #fff;
  padding: 20px;
  width: 90%;
  max-width: 400px;
  border-radius: 8px;
}

/* Estilos específicos para pantallas móviles */
@media (max-width: 576px) {
  .container {
    padding: 0.5rem;
  }

  .btn-sm {
    width: 100%;
    margin: 0.5rem 0;
  }

  .table {
    font-size: 0.8rem;
    display: block;
    overflow-x: auto;
    white-space: nowrap;
  }

  .action-button {
    font-size: 0.9rem;
    padding: 10px;
    margin: 0.2rem 0;
  }

  .card-header {
    font-size: 1rem;
    /* Reduce ligeramente el tamaño del encabezado */
  }

  .modal-content {
    width: 95%;
    max-width: 350px;
  }
}
</style>
